import { useState } from "react"

import {
  useCommonUserDestroy,
  useCommonUserRetrieveSuspense,
} from "@boundless/bl-api-client/common/services"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { Trash2Icon } from "lucide-react"
import mixpanel from "mixpanel-browser"
import { toast } from "sonner"

import { Button, LoadingButton } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { store } from "@/lib/store"

export function DeletionRequestDialog() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [checked, setChecked] = useState(false)

  const { data: user } = useCommonUserRetrieveSuspense()

  const { mutate: requestDeletion, isPending } = useCommonUserDestroy({
    mutation: {
      onSuccess: () => {
        toast.success("Account deletion requested successfully.")

        // The session is closed on "deletion", so handle it like a logout
        queryClient.clear()
        store.getState().resetAuth()
        mixpanel.reset()
        navigate({ to: "/login", replace: true })
      },
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="destructive" size="sm">
          <Trash2Icon className="mr-2 h-4 w-4" />
          Delete Account Request
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Account</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete the account linked to{" "}
            <span className="font-bold">{user.email}</span>?
          </DialogDescription>
        </DialogHeader>

        <div className="text-muted flex items-center gap-3 text-sm">
          <Checkbox
            id="confirm"
            checked={checked}
            onCheckedChange={(value) => setChecked(value === true)}
          />
          <Label htmlFor="confirm">
            {"I understand I won't be able to recover my account"}
          </Label>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          {isPending ? (
            <LoadingButton variant="destructive" />
          ) : (
            <Button
              variant="destructive"
              disabled={!checked}
              onClick={() => requestDeletion()}
            >
              Delete
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
